import { Box, IconButton, makeStyles, useTheme } from '@material-ui/core';
import StopIcon from '@material-ui/icons/Stop';
import {
  FC,
  FormEvent,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useState,
} from 'react';
import { getOnHoverScrollbarStyle } from '../../styles';
import { WbTextField } from '../WbTextField';
import { WbTooltip } from '../WbTooltip';
import { useChatContext } from './ChatContext';
import { AttachFileIcon, MicIcon, SendIcon } from './icons/icons';
import { WittyMessage } from './Messages';

type TextBoxProps = {
  isMicrophoneActive?: boolean;
  isAttachContentActive?: boolean;
  agentSelector?: ReactNode;
  statusAction?: ReactNode;
};

const useStyles = makeStyles(
  theme => ({
    composer: {
      width: '100%',
      minHeight: 98,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      background: theme.palette.background.default,
      border: `1px solid ${theme.palette.blueGrey[100]}`,
      borderRadius: 12,
      marginTop: theme.spacing(1.5),
      padding: theme.spacing(1, 1.5),
      overflow: 'hidden',
      '&:focus-within': {
        borderColor: theme.palette.blueGrey[100],
      },
      '& .MuiInput-underline:before, & .MuiInput-underline:after': {
        display: 'none',
      },
    },
    inputContainer: {
      alignItems: 'flex-start',
      maxHeight: '200px',
      minHeight: 46,
      padding: 0,
      overflowY: 'auto',
      ...getOnHoverScrollbarStyle(theme, false),
      '& .MuiOutlinedInput-notchedOutline': {
        border: '0 !important',
      },
    },
    chatInput: {
      padding: '0 !important',
      fontSize: 12,
      lineHeight: 1.5,
      overflowWrap: 'break-word' as const,
      wordBreak: 'break-word' as const,
      ...getOnHoverScrollbarStyle(theme, false),
    },
    actionRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: 32,
    },
    leftActions: {
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
      gap: theme.spacing(1),
    },
    actionButton: {
      width: 28,
      height: 28,
      padding: 2,
      borderRadius: 6,
    },
    sendIcon: {
      width: 24,
      height: 24,
    },
  }),
  { name: 'WittyTextBox' },
);

export const TextBox: FC<TextBoxProps> = ({
  isMicrophoneActive = true,
  isAttachContentActive = true,
  agentSelector,
  statusAction,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const { incomingMessage, responseInProgress, onNewMessage, onStop } =
    useChatContext();
  const busy = responseInProgress ?? incomingMessage;

  const [input, setInput] = useState<string>('');

  const submitMessage = useCallback(() => {
    if (!input || busy) return;
    const message: WittyMessage = { sender: 'you', contentMessage: input };
    setInput('');
    onNewMessage(message);
  }, [busy, input, onNewMessage]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitMessage();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitMessage();
    }
  };

  return (
    <form className={classes.composer} onSubmit={onSubmit}>
      <WbTextField
        fullWidth
        multiline
        variant="standard"
        rowsMax={6}
        placeholder="Type a message"
        ariaLabel="Type a message"
        value={input}
        onKeyDown={onKeyDown}
        onChange={event => {
          setInput(event.target.value);
        }}
        InputProps={{
          disableUnderline: true,
          classes: {
            root: classes.inputContainer,
            input: classes.chatInput,
          },
        }}
      />
      <Box className={classes.actionRow}>
        <Box className={classes.leftActions}>
          {isAttachContentActive && (
            <WbTooltip title="Attach file" placement="top">
              <IconButton size="small" aria-label="attach file">
                <AttachFileIcon
                  fontSize="small"
                  style={{ color: theme.palette.secondary.main }}
                />
              </IconButton>
            </WbTooltip>
          )}
          {isMicrophoneActive && (
            <WbTooltip title="Record voice message" placement="top">
              <IconButton size="small" aria-label="record">
                <MicIcon style={{ color: theme.palette.secondary.main }} />
              </IconButton>
            </WbTooltip>
          )}
          {agentSelector}
          {statusAction}
        </Box>
        {busy && onStop ? (
          <WbTooltip title="Stop response" placement="top">
            <IconButton
              className={classes.actionButton}
              size="small"
              type="button"
              aria-label="Stop Witty response"
              onClick={onStop}
            >
              <StopIcon color="primary" />
            </IconButton>
          </WbTooltip>
        ) : (
          <WbTooltip title="Send message" placement="top">
            <IconButton
              className={classes.actionButton}
              size="small"
              type="submit"
              aria-label="Send message to witty"
            >
              <SendIcon className={classes.sendIcon} />
            </IconButton>
          </WbTooltip>
        )}
      </Box>
    </form>
  );
};
